import { canonicalizeLocale, type VideoTrack } from '@course-authoring/shared-types';

/**
 * Learner caption-preference persistence + default-track resolution.
 *
 * On/off is independent of which track is shown. A stored preference always
 * wins; otherwise a non-English selected locale defaults captions on, and an
 * English locale follows the account `videoClosedCaption` flag. The displayed
 * track is a strict match of the selected locale — never a language pin, a
 * primary-subtag fallback, or the first eligible track.
 */

export const CAPTION_PREF_STORAGE_KEY = 'authorai.player.captionPref';

/** Stored sentinel meaning the learner explicitly disabled captions. */
export const CAPTION_PREF_OFF = 'off';

/** Stored sentinel meaning the learner explicitly enabled captions. */
export const CAPTION_PREF_ON = 'on';

/** Stored value after read-side normalization: `'on'`, `'off'`, or unset. */
export type CaptionPreference = typeof CAPTION_PREF_ON | typeof CAPTION_PREF_OFF | null;

/**
 * Normalizes a raw storage value. `'off'` stays off; any other non-empty
 * value (including `'on'` and a legacy track language code) is `'on'`.
 */
export function normalizeCaptionPref(raw: string | null | undefined): CaptionPreference {
  if (raw == null || raw === '') return null;
  return raw === CAPTION_PREF_OFF ? CAPTION_PREF_OFF : CAPTION_PREF_ON;
}

export function getStoredCaptionPref(): CaptionPreference {
  try {
    return normalizeCaptionPref(window.localStorage.getItem(CAPTION_PREF_STORAGE_KEY));
  } catch {
    return null;
  }
}

// Same-tab `storage` events don't fire, so co-mounted video renderers (one
// lesson can hold several) subscribe here to see each other's manual changes.
const captionPrefListeners = new Set<() => void>();

export function subscribeCaptionPref(listener: () => void): () => void {
  captionPrefListeners.add(listener);
  return () => {
    captionPrefListeners.delete(listener);
  };
}

export function setStoredCaptionPref(value: typeof CAPTION_PREF_ON | typeof CAPTION_PREF_OFF): void {
  try {
    window.localStorage.setItem(CAPTION_PREF_STORAGE_KEY, value);
  } catch {
    // Storage unavailable (private mode, quota) — persistence is best-effort.
  }
  for (const listener of captionPrefListeners) listener();
}

const CAPTION_KINDS = new Set(['captions', 'subtitles']);

/** Whether a track kind counts as closed captions (deliberately excludes chapters). */
export function isCaptionKind(kind: string): boolean {
  return CAPTION_KINDS.has(kind);
}

function isEligible(track: VideoTrack): boolean {
  return isCaptionKind(track.kind) && Boolean(track.src);
}

function primaryLanguageSubtag(locale: string | undefined): string {
  const canonical = canonicalizeLocale(locale);
  if (!canonical) return '';
  return canonical.split('-')[0] ?? '';
}

/**
 * Eligible track whose language equals `locale` after {@link canonicalizeLocale}.
 * No primary-subtag fallback and no first-track fallback. Returns -1 when none match.
 */
export function findTrackIndexByLanguage(tracks: VideoTrack[], locale: string): number {
  const target = canonicalizeLocale(locale);
  if (!target) return -1;
  return tracks.findIndex((t) => isEligible(t) && canonicalizeLocale(t.language) === target);
}

export interface DefaultCaptionOptions {
  /** Account-level "default closed captions" flag. */
  videoClosedCaption: boolean;
  /** The learner's persisted caption choice, if any (legacy language codes read as on). */
  storedPref: CaptionPreference | string | null;
  /** The learner's selected locale (for the default-on rule and track matching). */
  locale?: string;
}

/**
 * Whether captions should start on. Precedence: stored pref → non-English
 * selected locale → account `videoClosedCaption`.
 */
export function resolveCaptionsOn({
  videoClosedCaption,
  storedPref,
  locale,
}: DefaultCaptionOptions): boolean {
  const pref = normalizeCaptionPref(storedPref);
  if (pref === CAPTION_PREF_OFF) return false;
  if (pref === CAPTION_PREF_ON) return true;
  const primary = primaryLanguageSubtag(locale);
  if (primary && primary !== 'en') return true;
  return videoClosedCaption;
}

/**
 * Resolves which track (by index) should start showing, or -1 for none.
 *
 * On/off is {@link resolveCaptionsOn}; the track is a strict selected-locale
 * match. No matching track means captions stay off (no message, no fallback).
 */
export function resolveDefaultCaptionTrackIndex(
  tracks: VideoTrack[] | undefined,
  options: DefaultCaptionOptions,
): number {
  if (!tracks || tracks.length === 0) return -1;
  if (!resolveCaptionsOn(options)) return -1;
  if (!options.locale) return -1;
  return findTrackIndexByLanguage(tracks, options.locale);
}
